/**
 * Photo URLs from Google Places API (places.googleapis.com) often fail when loaded
 * directly from mobile clients (API key restrictions, CORS). Only photoUri
 * (lh3.googleusercontent.com) works reliably. Use the `/api/places/photo`
 * proxy route to fetch a working photoUri server-side (ADR-0034).
 */

/** True if URL looks like a Google Places API URL that may fail when loaded from client. */
export function isPlacesApiPhotoUrl(photoUrl: string | undefined): boolean {
  if (!photoUrl) return false;
  return (
    photoUrl.includes('places.googleapis.com') || photoUrl.includes('maps.googleapis.com')
  );
}

/** True if URL is a direct image URL (e.g. photoUri from getMedia) that works from any client. */
export function isDirectPhotoUrl(photoUrl: string | undefined): boolean {
  if (!photoUrl) return false;
  return photoUrl.includes('googleusercontent.com');
}

/**
 * Extracts placeId and photoRef from a stored Google Places photo URL.
 * Supports both legacy (maps.googleapis.com) and new (places.googleapis.com) formats.
 *
 * For v1 URLs the placeId is embedded in the URL itself
 * (`/places/X/photos/Y/media`), so the caller's `placeId` arg is
 * optional — we prefer what's in the URL. For legacy URLs
 * (`/place/photo?photo_reference=...`), the URL doesn't carry a
 * placeId, so the caller's arg is still required.
 *
 * Returning a parsed result lets the hook always route through
 * `/api/places/photo` instead of falling back to the Google URL
 * directly, which would bill the v1 Place Details Photos SKU per
 * client fetch.
 */
export function parsePlacePhotoRef(
  photoUrl: string | undefined,
  placeId: string | undefined
): { placeId: string; photoRef: string } | null {
  if (!photoUrl) return null;

  // New format: .../places/PLACE_ID/photos/PHOTO_REF/media. The URL
  // contains the placeId, so we don't need the caller to supply it.
  const newMatch = photoUrl.match(/\/places\/([^/]+)\/photos\/([^/]+)\/media/);
  if (newMatch) return { placeId: newMatch[1]!, photoRef: newMatch[2]! };

  // Legacy format: photo_reference=XXX in query. The URL doesn't
  // contain a placeId, so the caller's arg is required.
  if (placeId && photoUrl.includes('maps.googleapis.com')) {
    try {
      const url = new URL(photoUrl);
      const photoRef = url.searchParams.get('photo_reference');
      if (photoRef) return { placeId, photoRef };
    } catch {
      // ignore
    }
  }
  return null;
}
